// Importing required components and modules
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PropTypes from "prop-types";
import SearchIcon from "@mui/icons-material/Search";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import {
  Box,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PoTableHead from "./PoTableHead.jsx";
import PurchaseCss from "../PurchaseDetails/PurchaseDetails.module.css";
import { poDetails, vendorDetails } from "../../../serviceFile/ApiService.js";
import Buttons from "../../Components/Button/Buttons.jsx";
import PoList from "./PoList.jsx";
import PoTable from "./PoTable.jsx";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array?.map((el, index) => [el, index]);
  stabilizedThis?.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis?.map((el) => el[0]);
}
PoTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

// Define the Po list component
const PoDetails = () => {
  const isSmDown = useMediaQuery("(max-width: 900px)");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("");
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [data, setData] = useState();
  const [purchaseDetails, setPurchaseDetails] = useState();
  const [show, setShow] = useState(false);
  const rowsPerPage = 10;
  const [searchParams] = useSearchParams();
  const poNo = searchParams.get("poNo");
  const poIndex = searchParams.get("index");
  const poParseIndex = parseInt(poIndex, 10);
  const [poNumber, setPoNumber] = useState(poNo);
  const [selectedItemIndex, setSelectedItemIndex] = useState(poParseIndex);
  const navigate = useNavigate();

  // Api to fetch poList details
  useEffect(() => {
    poDetails(poNumber)
      .then((res) => {
        setData(res?.data?.results);
      })
      .catch((response) => {
        setData();
      });
  }, [poNumber]);

  useEffect(() => {
    vendorDetails().then((res) => {
      setPurchaseDetails(res?.data?.po_list);
    });
  }, []);

  // Sort function
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Functions to change pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // handle search value
  const handleSearch = (name, e) => {
    const value = e.target?.value;
    setSearchValue(value);
    setPage(1);
  };
  const filteredData = data?.filter((row) =>
    Object.values(row)?.some(
      (field) =>
        typeof field === "string" &&
        field.toLowerCase().includes(searchValue?.toLowerCase())
    )
  );

  // handle back button
  const handleBack = () => {
    navigate("/");
  };

  // Function to handle purchase code
  const handlePurchaseCode = (poValue, index) => {
    setSelectedItemIndex(index);
    setPoNumber(poValue);
    setShow(false);
    navigate({
      pathname: navigate.pathname,
      search: `?poNo=${poValue}&index=${index}`,
    });
  };

  // Function to handle po details display
  const handlePoDisplay = () => {
    setShow(!show);
  };

  // Function to handle background color of side bar
  const getBackgroundColor = (index, selectedItemIndex) => {
    if (index === selectedItemIndex) {
      return "steelblue";
    } else if (index % 2 === 0) {
      return "rgb(155, 209, 242)";
    } else {
      return "rgb(237 237 237)";
    }
  };

  // JSX code for rendering the Purchase List component
  return (
    <>
      <Helmet>
        <title>PO details</title>
      </Helmet>
      {/* The main container */}
      <Box
        sx={{
          width: "100%",
          marginBottom: isSmDown ? "70px" : "19px",
          marginTop: "60px",
          display: "flex",
        }}
      >
        <Stack
          direction={isSmDown ? "column" : "row"}
          spacing={3}
          width="100%"
          sx={{ padding: isSmDown ? "0" : "30px" }}
        >
          <Stack
            sx={{
              width: isSmDown ? "100%" : "20%",
              borderRadius: "10px",
              maxHeight: "80vh",
              overflowY: "scroll",
              overflowX: "hidden",
            }}
            className={PurchaseCss.profileCard}
          >
            <Stack
              sx={{
                padding: "20px",
                marginTop: "10px",
                borderRadius: "10px",
                marginRight: "50px",
              }}
              className={PurchaseCss.profileDetailsCard}
            >
              <Stack
                direction="row"
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    alignItems: "center",
                    color: "darkslategray",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    textShadow: "2px 2px 4px rgba(0, 0, 0, 0.1)",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}
                  title="Purchase orders"
                >
                  Purchase orders
                </Typography>
                <IconButton
                  aria-label="toggle menu"
                  onClick={handlePoDisplay}
                  edge="end"
                  sx={{ display: { sm: "block", md: "none" } }}
                >
                  {show ? (
                    <KeyboardArrowDownIcon
                      style={{ color: "rgb(64, 132, 255) " }}
                    />
                  ) : (
                    <MenuIcon style={{ color: "rgb(64, 132, 255) " }} />
                  )}
                </IconButton>
              </Stack>
              <PoList
                purchaseDetails={purchaseDetails}
                selectedItemIndex={selectedItemIndex}
                handlePurchaseCode={handlePurchaseCode}
                handlePoDisplay={handlePoDisplay}
                getBackgroundColor={getBackgroundColor}
                show={show}
              />
            </Stack>
          </Stack>
          <Stack
            sx={{
              width: isSmDown ? "100%" : "80%",
              padding: "20px",
              marginTop: "20px",
              borderRadius: "10px",
              marginRight: "50px",
            }}
            className={PurchaseCss.purchaseTableCard}
          >
            <Stack
              direction={isSmDown ? "column" : "row"}
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography
                variant="h6"
                title={poNumber}
                sx={{
                  alignItems: "center",
                  color: "darkslategray",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  textShadow: "2px 2px 4px rgba(0, 0, 0, 0.1)",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                Purchase No: {poNumber}
              </Typography>
              <Stack
                direction={isSmDown ? "column" : "row"}
                spacing={1}
                sx={{
                  marginBottom: "10px",
                  justifyContent: "end",
                  display: "flex",
                }}
              >
                <TextField
                  id="outlined-basic"
                  label="Search"
                  variant="outlined"
                  autoComplete="off"
                  type="text"
                  size="small"
                  margin="dense"
                  sx={{ width: { xs: "100%", sm: "300px" } }}
                  onChange={(e) => handleSearch("search", e)}
                  InputProps={{
                    shrink: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon style={{ color: "rgb(64, 132, 255) " }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Buttons
                  onClick={handleBack}
                  sx={{
                    width: "100px",
                    height: "40px",
                  }}
                  text="Back"
                  endIcon={<ChevronLeftIcon />}
                />
              </Stack>
            </Stack>
            <Stack spacing={1}>
              <div className={PurchaseCss.empcard}>
                <Paper sx={{ width: "100%", mb: 2 }}>
                  <PoTable
                    filteredData={filteredData}
                    order={order}
                    orderBy={orderBy}
                    getComparator={getComparator}
                    stableSort={stableSort}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    handleRequestSort={handleRequestSort}
                    isSmDown={isSmDown}
                  />
                </Paper>
              </div>
            </Stack>
            {filteredData?.length > 0 && (
              <Stack
                spacing={1}
                sx={{ alignItems: "flex-end", marginTop: "1px" }}
              >
                <Pagination
                  siblingCount={0}
                  count={
                    filteredData?.length <= 10
                      ? 1
                      : Math.ceil(filteredData?.length / 10)
                  }
                  page={page}
                  onChange={handleChangePage}
                  color="primary"
                  variant="text"
                  showFirstButton
                  showLastButton
                />
              </Stack>
            )}
          </Stack>
        </Stack>
      </Box>
    </>
  );
};
export default PoDetails;
